import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, addDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../firebase';
import './MeditacionPlayerScreen.css';
import { FaPlay, FaPause, FaStepBackward, FaStepForward, FaBackward, FaForward, FaTimes } from 'react-icons/fa';
import Header from '../components/Header';
import CosmicBackground from '../components/CosmicBackground';
import { logEvent } from '../analytics';
import meditaZenthorImage from '../images/medita_zenthor.jpg';

const MeditacionPlayerScreen = () => {
  const { meditacionId } = useParams();
  const navigate = useNavigate();
  const [meditacion, setMeditacion] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const fetchMeditacion = async () => {
      const docRef = doc(db, 'content', meditacionId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setMeditacion(data);
        if (data.audio_url) {
          const storageRef = ref(storage, data.audio_url);
          const url = await getDownloadURL(storageRef);
          setAudioUrl(url);
        }
      } else {
        console.log('No such document!');
      }
    };
    fetchMeditacion();
  }, [meditacionId]);

  const savePlayEventToFirestore = async (meditacionName) => {
    try {
      const userId = auth.currentUser?.uid || localStorage.getItem('user_id') || 'anonymous';

      await addDoc(collection(db, 'meditaciones_uso'), {
        meditacion_name: meditacionName,
        user_id: userId,
        clicked_at: new Date()
      });

      console.log('Meditation play event saved to Firestore');
    } catch (error) {
      console.error('Error saving meditation play event:', error);
    }
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      audioRef.current.pause();
      // Track pause event
      logEvent('Meditation', 'Pause', meditacion?.name || 'Unknown Meditation');
    } else {
      audioRef.current.play();
      // Track play event
      logEvent('Meditation', 'Play', meditacion?.name || 'Unknown Meditation');

      // Save play event to Firestore asynchronously (fire-and-forget)
      savePlayEventToFirestore(meditacion?.name || 'Unknown Meditation');
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current && audioRef.current.duration) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration);
      const newProgress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      if (isFinite(newProgress)) {
        setProgress(newProgress);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    // Track meditation completion
    logEvent('Meditation', 'Complete', meditacion?.name || 'Unknown Meditation');
  };

  const handleSeek = (e) => {
    const percent = e.target.value;
    if (audioRef.current && duration) {
      audioRef.current.currentTime = (percent / 100) * duration;
    }
  };

  const handleSkip = (seconds) => {
    if (audioRef.current) {
      let newTime = audioRef.current.currentTime + seconds;
      if (newTime < 0) newTime = 0;
      if (newTime > duration) newTime = duration;
      audioRef.current.currentTime = newTime;
    }
  };

  const handleClose = () => {
    // Track meditation exit
    logEvent('Meditation', 'Exit', meditacion?.name || 'Unknown Meditation');
    navigate(-1);
  };

  const formatTime = (sec) => {
    if (!sec || isNaN(sec)) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  if (!meditacion) {
    return <div className="loading_meditacion_express">Cargando...</div>;
  }

  return (
    <div className="meditacion-player-root">
      <CosmicBackground />
      <button className="close-btn" onClick={handleClose}><FaTimes /></button>
      <div className="meditacion-player-content">
        <div className="meditacion-player-image" style={{ backgroundImage: `url(${meditacion.image_url || meditaZenthorImage})` }} />
        <div className="meditacion-player-title-section">
          <div className="meditacion-player-title">{meditacion.name}</div>
          <div className="meditacion-player-author">{meditacion.author || 'Zenthor Team'}</div>
        </div>
        <div className="meditacion-player-progress-section">
          <input
            type="range"
            min="0"
            max="100"
            value={progress}
            onChange={handleSeek}
            className="meditacion-player-progress-bar"
          />
          <div className="meditacion-player-time-row">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
        <div className="meditacion-player-controls">
          <button className="meditacion-player-btn" onClick={() => handleSkip(-duration)}><FaStepBackward /></button>
          <button className="meditacion-player-btn" onClick={() => handleSkip(-10)}><FaBackward /></button>
          <button className="meditacion-player-btn play-main" onClick={togglePlayPause}>
            {isPlaying ? <FaPause /> : <FaPlay />}
          </button>
          <button className="meditacion-player-btn" onClick={() => handleSkip(10)}><FaForward /></button>
          <button className="meditacion-player-btn" onClick={() => handleSkip(duration)}><FaStepForward /></button>
        </div>
        <div className="meditacion-player-recommend">Te recomendamos hacer el ejercicio con audífonos.</div>
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
        ></audio>
      </div>
    </div>
  );
};

export default MeditacionPlayerScreen; 